import Immutable from 'immutable';
import {fromJSOrdered} from '~/utils/immutable';
import {DEFAULT_COLOR_ID, DEFAULT_SIZES} from '~/constants/ProductConstants';
import {currentColorUrlOverride} from '~/utils/url';
// types
import CatalogTypes from '~/constants/types/CatalogTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import QuoteTypes from '~/constants/types/QuoteTypes';
import {makeUndoable} from '~/utils/undoable';

export function initQuantities(sizes = DEFAULT_SIZES) {
  return Immutable.OrderedMap(sizes.map(k => [k, 0]));
}

export function initialState() {
  return Immutable.Map({
    caveats: Immutable.List(),
    colorfulQuote: false,
    colorCount: Immutable.Map({
      back: null,
      front: null
    }),
    droppedView: null,
    embroideryToPrintPrice: null,
    hasFullQuote: false,
    isFetchingPricesWithoutDroppedView: false,
    itemizedPrices: Immutable.Map(),
    itemizedPricesWithoutDroppedView: Immutable.Map(),
    mode: '',
    ncoEligible: null,
    oneColorLessPrice: null,
    oneViewLessPrice: null,
    postalCode: null,
    quantities: Immutable.Map({
      [currentColorUrlOverride(DEFAULT_COLOR_ID)]: Immutable.Map()
    }),
    quoteBlank: false,
    realTimeItemizedPrices: Immutable.Map(),
    realTimeTotalPrice: null,
    shipping: Immutable.Map(),
    showShippingForm: true,
    sleeveViewLessPrice: null,
    sleeveViewLessSaveAmount: null,
    swappedPrintMethod: null,
    totalPrice: null,
    touchedQuantities: false,
    unavailableCoreSizes: null,
    volumeDiscountPrice: null
  });
}

const copyValidSizeQuantities = function (
  quantities,
  validSizes,
  previousColorId,
  nextColorId
) {
  const nextProductQuantities = validSizes.reduce((productQtys, size) => {
    return productQtys.set(size, quantities.getIn([previousColorId, size], 0));
  }, Immutable.OrderedMap());
  return quantities
    .set(nextColorId, nextProductQuantities)
    .delete(previousColorId);
};

export function quote(state = initialState(), action) {
  switch (action.type) {
    case QuoteTypes.SHOW_SHIPPING_FORM:
      return state.set('showShippingForm', action.payload);
    case QuoteTypes.UPDATE_COLORFUL_QUOTE:
      return state.set('colorfulQuote', action.payload);
    case QuoteTypes.UPDATE_COLOR_COUNT:
      return state.set('colorCount', Immutable.Map(action.payload));
    case QuoteTypes.UPDATE_NCO_ELIGIBILITY:
      return state.set('ncoEligible', action.payload);
    case QuoteTypes.UPDATE_DROPPED_VIEW:
      return state.set('droppedView', action.payload);
    case QuoteTypes.UPDATE_HAS_FULL_QUOTE:
      return state.set('hasFullQuote', action.payload);
    case QuoteTypes.UPDATE_IS_FETCHING_PRICES_WITHOUT_DROPPED_VIEW:
      return state.set('isFetchingPricesWithoutDroppedView', action.payload);
    case QuoteTypes.UPDATE_IS_INVENTORY_SHIPPING_TYPE:
      return state.set('isInventoryShippingType', action.payload);
    case QuoteTypes.UPDATE_POSTAL_CODE:
      return state.set('postalCode', action.payload);
    case QuoteTypes.UPDATE_QUOTE:
      return state.merge(action.payload);
    case QuoteTypes.UPDATE_QUOTE_BLANK:
      return state.set('quoteBlank', action.payload);
    case QuoteTypes.UPDATE_MODE:
      return state.set('mode', action.payload);
    case QuoteTypes.UPDATE_TOTAL_PRICE: {
      const {totalPrice, volumeDiscountPrice} = action.payload;
      const totalPriceState = state.set('totalPrice', parseFloat(totalPrice));
      if (volumeDiscountPrice) {
        return totalPriceState.set(
          'volumeDiscountPrice',
          parseFloat(volumeDiscountPrice)
        );
      }
      return totalPriceState;
    }
    case QuoteTypes.UPDATE_ITEMIZED_PRICES: {
      const itemizedPrices = action.payload;
      return state.set('itemizedPrices', Immutable.Map(itemizedPrices));
    }
    case QuoteTypes.UPDATE_ITEMIZED_PRICES_WITHOUT_DROPPED_VIEW: {
      const newItemizedPricesWithoutDroppedView = action.payload;

      if (!newItemizedPricesWithoutDroppedView) {
        return state.set('itemizedPricesWithoutDroppedView', Immutable.Map());
      }

      return state.update(
        'itemizedPricesWithoutDroppedView',
        currentItemizedPricesWithoutDroppedView =>
          currentItemizedPricesWithoutDroppedView.merge(
            Immutable.Map(newItemizedPricesWithoutDroppedView)
          )
      );
    }
    case QuoteTypes.UPDATE_ONE_COLOR_LESS_PRICE:
      return state.set('oneColorLessPrice', action.payload);
    case QuoteTypes.UPDATE_ONE_VIEW_LESS_PRICE:
      return state.set('oneViewLessPrice', action.payload);
    case QuoteTypes.UPDATE_SLEEVE_VIEW_LESS_PRICE:
      return state.set('sleeveViewLessPrice', action.payload);
    case QuoteTypes.UPDATE_EMBROIDERY_TO_PRINT_PRICE:
      return state.set('embroideryToPrintPrice', action.payload);
    case QuoteTypes.UPDATE_SLEEVE_VIEW_LESS_SAVE_AMOUNT:
      return state.set('sleeveViewLessSaveAmount', action.payload);
    case QuoteTypes.UPDATE_PRODUCT_QUANTITIES: {
      const quantities = action.payload;
      let newState = state;

      Object.keys(quantities).forEach(productKey => {
        const sizes = quantities[productKey];
        if (sizes) {
          Object.keys(sizes).forEach(size => {
            newState = newState.setIn(
              ['quantities', productKey, size],
              sizes[size]
            );
          });
        }
      });
      return newState.set('touchedQuantities', true);
    }
    case QuoteTypes.UPDATE_REAL_TIME_ITEMIZED_PRICES: {
      const itemizedPrices = action.payload;
      return state.update('realTimeItemizedPrices', currentItemizedPrices =>
        currentItemizedPrices.merge(Immutable.Map(itemizedPrices))
      );
    }
    case QuoteTypes.UPDATE_REAL_TIME_TOTAL_PRICE: {
      const {totalPrice} = action.payload;
      return state.set('realTimeTotalPrice', parseFloat(totalPrice));
    }
    case QuoteTypes.UPDATE_SHIPPING:
      return state.set(
        'shipping',
        Immutable.Map({
          ...action.payload,
          standardDelivery: Immutable.Map(action.payload.standardDelivery),
          rushDelivery: Immutable.Map(action.payload.rushDelivery),
          superRushDelivery: Immutable.Map(action.payload.superRushDelivery)
        })
      );
    case QuoteTypes.REAL_TIME_PRICING_ERROR:
      return state.setIn(
        ['realTimeItemizedPrices', action.payload.selectedProductId],
        Immutable.Map({itemPrice: null, subtotal: null, error: true})
      );
    case DesignTypes.CLEAR_DESIGN_DATA: {
      return state.merge({
        colorfulQuote: false,
        hasFullQuote: false
      });
    }
    case DesignTypes.FETCH_SUCCESS: {
      const {postalCode, quantities, totalPrice} = action.payload.quoteData;
      if (action.payload.changeProductQuantities) {
        return state.merge({
          hasFullQuote: !!totalPrice,
          postalCode: postalCode,
          quantities: fromJSOrdered(quantities),
          totalPrice: totalPrice,
          touchedQuantities: true
        });
      } else {
        return state;
      }
    }
    case ProductTypes.RESET: {
      const {colorId, sizes} = action.payload;
      return initialState().update('quantities', () => {
        if (sizes && sizes.length) {
          return Immutable.Map({[colorId]: initQuantities(sizes)});
        } else return Immutable.Map();
      });
    }
    case CatalogTypes.STYLES_SUCCESS: {
      const styles = action.payload;
      state.get('quantities').mapKeys(colorId => {
        styles.find(s => {
          const color = s.colors.find(c => c.id === colorId);
          const hasColorQuantities = state.getIn(['quantities', colorId])?.size;
          if (color && !hasColorQuantities) {
            state = state.setIn(
              ['quantities', colorId],
              initQuantities(color.sizes)
            );
          }
        });
      });
      return state;
    }
    case ProductTypes.ADD_PRODUCT: {
      const {products} = action.payload;
      return state.update('quantities', quantities => {
        return quantities.withMutations(quantities => {
          products.forEach(p => {
            const productSizes = initQuantities(p.validSizes);
            return quantities.set(p.colorId, productSizes);
          });
          return quantities;
        });
      });
    }
    case ProductTypes.DELETE_PRODUCT: {
      const {colorId} = action.payload;
      return state.deleteIn(['quantities', colorId]);
    }
    case ProductTypes.SWAP_PRODUCT: {
      const {nextColorIds, validSizes} = action.payload;
      const {clearPreviousProducts} = action.meta;
      return state.update('quantities', quantities => {
        return Object.keys(quantities.toObject()).reduce(
          (quantities, previousColorId) => {
            const nextColorId = nextColorIds[previousColorId];
            if (nextColorId) {
              return copyValidSizeQuantities(
                quantities,
                validSizes[nextColorId],
                previousColorId,
                nextColorId
              );
            } else if (clearPreviousProducts) {
              return quantities.delete(previousColorId);
            } else {
              return quantities;
            }
          },
          quantities
        );
      });
    }
    case ProductTypes.CHANGE_COLOR: {
      const {previousColorId, nextColorId, validSizes} = action.payload;
      return state.update('quantities', quantities => {
        return copyValidSizeQuantities(
          quantities,
          validSizes,
          previousColorId,
          nextColorId
        );
      });
    }

    case DesignTypes.SET_COMBINE_DESIGNS: {
      const {
        currentColorId,
        isCombined,
        previousQuoteQuantities,
        shouldInitQuantities
      } = action.payload;

      if (isCombined && currentColorId && previousQuoteQuantities) {
        return state.update('quantities', quantities => {
          return quantities.withMutations(quantities => {
            if (shouldInitQuantities) {
              const currentColorQuantities = quantities
                .get(currentColorId)
                .toObject();
              const currentColorSizes = Object.keys(currentColorQuantities);
              quantities.set(currentColorId, initQuantities(currentColorSizes));
            }

            previousQuoteQuantities.forEach(previousProduct => {
              const sizes = Object.keys(
                quantities.get(previousProduct.colorId).toObject()
              );
              const updatedQuantities = Immutable.OrderedMap(
                sizes.map(k => {
                  if (previousProduct.quoteQuantities[k]) {
                    return [k, previousProduct.quoteQuantities[k]];
                  } else {
                    return [k, 0];
                  }
                })
              );
              return quantities.set(previousProduct.colorId, updatedQuantities);
            });
            return quantities;
          });
        });
      } else {
        return state;
      }
    }
    case QuoteTypes.UPDATE_UNAVAILABLE_CORE_SIZES: {
      return state.set('unavailableCoreSizes', action.payload);
    }

    case QuoteTypes.SET_SWAPPED_PRINT_METHOD:
      return state.set('swappedPrintMethod', action.payload);

    default:
      return state;
  }
}

export default makeUndoable(quote);
